import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Clock, HeartHandshake, GraduationCap } from "lucide-react";
import api, { apiError } from "../lib/api";
import { YEAR_PRESETS, PRESET_LABELS_LONG } from "../lib/yearPresets";
import { useAuth } from "../context/AuthContext";

const PRESET_LABELS = PRESET_LABELS_LONG;

export default function ParentAuth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState("register");
  const [accountType, setAccountType] = useState(params.get("type") === "student" ? "student" : "parent");
  const [form, setForm] = useState({ name: "", email: "", password: "", child_name: "", child_year_group: "", year_system: "scottish" });
  const [busy, setBusy] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const { saveSession } = useAuth();
  const navigate = useNavigate();
  const isStudent = accountType === "student";

  const forgotSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/forgot-password", { email: form.email, role: "parent", origin_url: window.location.origin });
      setForgotSent(true);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = mode === "register"
        ? await api.post("/parent/auth/register", { ...form, account_type: accountType })
        : await api.post("/parent/auth/login", { email: form.email, password: form.password });
      saveSession(data);
      toast.success(mode === "register" ? "Account created!" : `Welcome back, ${data.user.name}!`);
      navigate("/parent");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F9A8D4]/30" data-testid="parent-auth-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="parent-auth-brand">
          <div className="w-9 h-9 rounded-xl border-2 border-zinc-900 bg-white flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
            <Clock className="w-5 h-5" />
          </div>
          <span className="font-display font-black text-2xl">MyTimetable.co.uk</span>
        </Link>

        <div className="nb-card p-8 bg-white">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-xl border-2 border-zinc-900 bg-[#F9A8D4] flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
              {isStudent ? <GraduationCap className="w-7 h-7" /> : <HeartHandshake className="w-7 h-7" />}
            </div>
            <h1 className="font-display font-black text-2xl mt-4" data-testid="parent-auth-heading">
              {isStudent ? "For students" : "For parents"}
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              {isStudent
                ? "At college, uni or learning as an adult? Build your own timetable and keep exams, study days and notes in one place."
                : "Your child's school isn't on MyTimetable.co.uk? No problem — build their timetable yourself and keep it all in one place."}
            </p>
          </div>

          <div className="flex rounded-xl border-2 border-zinc-900 overflow-hidden mb-6" role="tablist">
            {[["register", "Create account"], ["login", "Log in"]].map(([m, label]) => (
              <button key={m} type="button" role="tab" data-testid={`parent-${m}-tab`}
                onClick={() => setMode(m)}
                className={`flex-1 py-2.5 font-display font-bold text-sm transition-colors duration-150 ${mode === m ? "bg-[#F9A8D4]" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}>
                {label}
              </button>
            ))}
          </div>

          {mode === "register" && (
            <div className="flex rounded-xl border-2 border-zinc-900 overflow-hidden mb-6" data-testid="account-type-toggle">
              {[["parent", "I'm a parent"], ["student", "I'm a student"]].map(([t, label]) => (
                <button key={t} type="button" data-testid={`account-type-${t}`}
                  onClick={() => setAccountType(t)}
                  className={`flex-1 py-2.5 font-display font-bold text-sm transition-colors duration-150 ${accountType === t ? "bg-[#A7E4D3]" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}>
                  {label}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <label className="nb-label" htmlFor="parent-name">Your name</label>
                  <input id="parent-name" className="nb-input mt-2" data-testid="parent-name-input" placeholder="e.g. Alex Murray"
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} />
                </div>
                {!isStudent && (
                  <div>
                    <label className="nb-label" htmlFor="parent-child-name">Child's name</label>
                    <input id="parent-child-name" className="nb-input mt-2" data-testid="parent-child-name-input" placeholder="e.g. Sophie"
                      value={form.child_name} onChange={(e) => setForm({ ...form, child_name: e.target.value })} required minLength={2} />
                    <p className="text-xs text-zinc-400 font-semibold mt-1">You can add more children later (£5/yr or £20 on the 6-year plan each).</p>
                  </div>
                )}
                <div>
                  <label className="nb-label" htmlFor="parent-year-system">Year group naming</label>
                  <select id="parent-year-system" className="nb-input mt-2" data-testid="parent-year-system-select"
                    value={form.year_system} onChange={(e) => setForm({ ...form, year_system: e.target.value, child_year_group: "" })}>
                    {Object.entries(PRESET_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="nb-label" htmlFor="parent-child-year">{isStudent ? "Your year group (optional)" : "Child's year group (optional)"}</label>
                  <select id="parent-child-year" className="nb-input mt-2" data-testid="parent-child-year-select"
                    value={form.child_year_group} onChange={(e) => setForm({ ...form, child_year_group: e.target.value })}>
                    <option value="">Choose…</option>
                    {YEAR_PRESETS[form.year_system].map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </>
            )}
            <div>
              <label className="nb-label" htmlFor="parent-email">Email</label>
              <input id="parent-email" type="email" className="nb-input mt-2" data-testid="parent-email-input" placeholder="you@example.com"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            </div>
            <div>
              <label className="nb-label" htmlFor="parent-password">Password</label>
              <input id="parent-password" type="password" className="nb-input mt-2" data-testid="parent-password-input"
                placeholder={mode === "register" ? "At least 8 characters" : "Your password"}
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={mode === "register" ? 8 : 1} />
            </div>
            <button type="submit" disabled={busy} data-testid="parent-auth-submit" className="nb-btn w-full bg-[#F9A8D4] py-3">
              {busy ? "One moment…" : mode === "register" ? "Create account" : "Log in"}
            </button>
          </form>
          {mode === "login" && !showForgot && (
            <button type="button" onClick={() => setShowForgot(true)} data-testid="parent-forgot-link"
              className="w-full text-center text-xs font-bold text-zinc-500 hover:text-zinc-900 mt-4 transition-colors duration-150">
              Forgot your password?
            </button>
          )}
          {mode === "login" && showForgot && (
            <div className="mt-4 pt-4 border-t-2 border-zinc-100" data-testid="parent-forgot-form">
              {forgotSent ? (
                <p className="text-sm font-bold text-zinc-600 text-center" data-testid="parent-forgot-sent">If an account exists for that email, a reset link is on its way. It lasts 1 hour.</p>
              ) : (
                <form onSubmit={forgotSubmit} className="flex gap-2">
                  <input type="email" className="nb-input flex-1" data-testid="parent-forgot-email" placeholder="Your email"
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                  <button type="submit" data-testid="parent-forgot-submit" className="nb-btn bg-[#FDE68A] px-4 py-2 text-sm shrink-0">Email reset link</button>
                </form>
              )}
            </div>
          )}
          {mode === "register" && (
            <p className="text-xs text-zinc-400 mt-4 text-center">A small yearly subscription (set by your plan, from £9.99/yr) unlocks the timetable builder.</p>
          )}
        </div>
      </div>
    </div>
  );
}
